/**
 * Same-origin, root-relative path validation shared by every "redirect to a
 * caller-supplied path" flow: the report create-and-return round trip
 * (`useReturnTo`), metric creation's return path (`metricCreation.js`), and
 * the login page's post-auth redirect. Each of these reads a path out of a
 * query param an attacker could craft, so it must be validated before it is
 * ever passed to `navigate()` - otherwise it's an open redirect.
 */

/**
 * @param {unknown} path
 * @returns {boolean} Whether `path` is safe to `navigate()` to.
 */
export function isSafeRelativePath(path) {
  if (typeof path !== 'string' || path.length === 0) return false;
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//') || path.startsWith('/\\')) return false;
  // Tab, CR and LF are silently stripped by the WHATWG URL parser wherever
  // they occur in the string (not just at the ends), so a value like
  // `/\t/evil.example.com` doesn't *look* protocol-relative but parses as
  // `//evil.example.com`. Reject that and every other C0 control character
  // up front rather than relying solely on the prefix checks above.
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f\x7f]/.test(path)) return false;

  // Belt-and-suspenders: ask the platform's own parser what origin this
  // would actually resolve to, so any bypass class the checks above don't
  // yet know about still gets caught.
  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    return new URL(path, origin).origin === origin;
  } catch {
    return false;
  }
}

export default isSafeRelativePath;
